/**
 * Core messages (English is the source of truth and must be complete). Module screens keep their
 * own catalogs next to their code; only strings the shell needs before a module loads, such as
 * sidebar labels, live here.
 */
export const en = {
  "files.title": "Files",
  "files.subtitle": "Club documents with retained versions and a clear history.",
  "files.upload": "Upload file",
  "files.replace": "Upload new version",
  "files.name": "File title",
  "files.choose": "Choose file",
  "files.hint": "Up to 50 MB. Earlier versions are kept. Programs and scripts are refused.",
  "files.versions": "Version history",
  "files.version": "Version {version}",
  "files.download": "Download version {version}",
  "files.restore": "Restore version {version} as current",
  "files.empty": "No club files yet",
  "files.search": "Search club files",
  "files.by": "Uploaded by {name}",
  "files.saved": "File version saved.",
  "files.size": "{bytes} bytes",
  "files.open": "Open {title}",
  "files.storage": "Files currently use the installation’s backed-up document store. External SMB and S3 connectors require setup.",

  "workspace.metric.active_members": "Active members",
  "workspace.metric.spend_mtd": "Spend this month",
  "workspace.metric.rounds_mtd": "Rounds this month",
  "workspace.metric.ar_over_60": "Receivables over 60 days",
  "workspace.metric.ar_over_90": "Receivables over 90 days",
  "workspace.metric.spend_trend": "Spend by month and department",
  "workspace.metric.rounds_trend": "Rounds played by month",
  "workspace.metric.members_by_category": "Active members by category",
  "workspace.metric.membership_changes": "New members and resignations",
  "workspace.metric.top_spenders": "Top spenders this year",
  "workspace.metric.departments": "Spend by department this year",
  "workspace.metric.most_rounds": "Most rounds this year",

  "workspace.background.title": "Dashboard background",
  "workspace.background.hint": "Choose a scenic club photograph. PNG, JPEG or WebP, up to 10 MB. This image is public club branding; do not upload confidential information.",
  "workspace.background.alt": "Dashboard photograph for {club}",
  "workspace.background.choose": "Choose dashboard photograph",
  "workspace.background.invalid": "Choose a PNG, JPEG or WebP image smaller than 10 MB.",
  "workspace.background.upload": "Upload photograph",
  "workspace.background.replace": "Replace photograph",
  "workspace.background.remove": "Remove photograph",
  "workspace.background.removeHint": "Return the dashboard to the standard theme for everyone in this club?",
  "workspace.background.saved": "Dashboard photograph saved.",
  "workspace.editor": "Dashboard editor",
  "workspace.editor.hint": "Show, hide and reorder your blocks. These choices follow your account in this club.",
  "workspace.editor.show": "Show {block}",
  "workspace.editor.up": "Move {block} earlier",
  "workspace.editor.down": "Move {block} later",
  "workspace.editor.saved": "Your dashboard has been saved.",
  "workspace.editor.empty": "Your dashboard is clear. Use the dashboard editor to add blocks.",
  "workspace.editor.reload": "Reload saved dashboard",
  "workspace.editor.reset": "Show all blocks",
  "workspace.wizard": "Dashboard Wizard",
  "workspace.wizard.hint": "Let’s shape your workspace around your day. Your choices change your dashboard, never your access.",
  "workspace.wizard.position": "What is your position?",
  "workspace.wizard.focus": "What will you focus on?",
  "workspace.wizard.analytics": "Which information matters to you?",
  "workspace.wizard.later": "Set up later",
  "workspace.wizard.finish": "Create my dashboard",
  "workspace.wizard.back": "Back",
  "workspace.wizard.next": "Continue",
  "workspace.wizard.step": "Step {step} of 3",
  "workspace.wizard.noAnalytics": "Your current role does not include analytics. Your administrator can update your access.",
  "workspace.position.management": "Club management",
  "workspace.position.membership": "Membership and administration",
  "workspace.position.golf": "Golf and pro shop",
  "workspace.position.hospitality": "Hospitality and front desk",
  "workspace.position.finance": "Finance and accounting",
  "workspace.position.operations": "Operations",
  "workspace.position.other": "Other / customize my own",
  "workspace.online_one": "{count} user online",
  "workspace.online_other": "{count} users online",
  "workspace.online.title": "People at your club",
  "workspace.online.hint": "Active in ClubCore during the last two minutes. Signed-out and expired sessions disappear automatically.",
  "workspace.online.loading": "Checking who is online…",
  "workspace.online.unavailable": "Online status unavailable",
  "workspace.online.none": "No other active users right now.",
  "workspace.chat": "Message {name}",
  "workspace.chat.placeholder": "Write a message…",
  "workspace.chat.send": "Send message",
  "workspace.chat.empty": "Start a conversation with your colleague.",
  "workspace.chat.unread": "{count} unread messages",
  "workspace.chat.sent": "Message sent",
  "workspace.chat.you": "You",
  "workspace.release": "ClubCore by SiteKings · {version}",
  "workspace.release.title": "What’s new in ClubCore",
  "workspace.release.components": "Installed components",
  "workspace.release.history": "Recent releases",
  "workspace.alerts.title": "Notifications and messages",
  "workspace.alerts.empty": "You’re all caught up.",
  "workspace.alerts.all": "All notifications",
  "workspace.alerts.unread": "{count} unread alerts and messages",
  "workspace.analytics.title": "Analytics",

  "a11y.skipToContent": "Skip to content",

  "access.deniedTitle": "You do not have access to this page",
  "access.goHome": "Go to home",
  "access.deniedBody": "Ask a club administrator to give your role the permission this page needs.",

  "auth.microsoft": "Sign in with Microsoft 365",
  "auth.microsoftFailed": "Microsoft sign-in could not be completed. Return to sign-in and try again, or contact your administrator to link your account.",
  "auth.localAdmin": "Local sign-in",
  "auth.localAdminHint": "Administrators can always use their local ClubCore account, including when Microsoft sign-in is unavailable.",
  "auth.siteKings": "Sign in with SiteKings",
  "auth.localSignIn": "Or use your ClubCore account",
  "auth.siteKingsFailed": "SiteKings sign-in could not be completed. Return to sign-in and try again.",
  "auth.signInTitle": "Sign in",
  "auth.signIn": "Sign in",
  "auth.signOut": "Sign out",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.invalid": "Invalid email or password.",
  "auth.haveResetCode": "Have a password reset code?",
  "auth.expired": "Your session ended. Sign in again to continue.",


  "club.chooseTitle": "Choose a club",
  "club.chooseBody": "You belong to more than one club. Pick the one you are working in.",
  "club.chooseStaff": "Platform access: pick the club to work in. Every request is recorded in that club's audit log.",
  "club.search": "Search clubs",
  "club.noneFound": "No clubs found",
  "club.switch": "Switch club",
  "club.switchLabel": "Current club: {club}. Switch club",
  "club.allClubs": "All clubs…",
  "club.noAccessTitle": "No club access",
  "club.noAccessBody": "Your account is not a member of any club yet. Ask a club administrator to add you.",

  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.loading": "Loading…",
  "common.loadMore": "Load more",
  "common.loadingMore": "Loading more…",
  "common.retry": "Try again",

  "error.title": "Something went wrong",
  "error.network": "Could not reach the server. Check the connection and try again.",
  "error.forbidden": "You do not have permission to do that.",
  "error.notFound": "That could not be found.",
  "error.server": "The server had a problem. Try again in a moment.",
  "error.rateLimited": "Too many attempts. Try again later.",
  "error.rateLimitedMinutes_one": "Too many attempts. Try again in {minutes} minute.",
  "error.rateLimitedMinutes_other": "Too many attempts. Try again in {minutes} minutes.",
  "error.unknown": "An unexpected error occurred.",

  "home.welcome": "Welcome, {name}",
  "home.subtitle": "{club}",
  "home.nothing": "Your role has no screens yet. Ask a club administrator for access.",
  "home.workspace": "Your club workspace",
  "home.more": "More tools ({count})",

  "language.label": "Language",

  "module.members": "Members",
  "module.admin": "Administration",
  "module.teesheet": "Tee sheet",
  "module.importers": "Imports",
  "module.billing": "Billing",
  "module.accounting": "Accounting",
  "module.expenses": "Expenses and reports",
  "module.documents": "Documents",
  "module.guests": "Guests",
  "module.frontdesk": "Front desk",
  "module.pos": "Point of sale",
  "module.lodging": "Lodging",
  "module.reports": "Reports",
  "module.portal": "Member portal",
  "module.notifications": "Notifications",
  "members.nav.households": "Households",
  "members.nav.search": "Find a member",
  "members.nav.categories": "Categories",
  "members.nav.rangePlan": "Range plan check",
  "members.nav.settings": "Member settings",
  "importers.nav.runs": "Import history",
  "importers.nav.reconciliation": "Jonas reconciliation",
  "billing.nav.accounts": "Accounts",
  "billing.nav.settings": "Billing settings",
  "accounting.nav.runs": "Billing runs",
  "accounting.nav.minimum": "Minimum close",
  "accounting.nav.statements": "Statement close",
  "ledger.nav.accounts": "Chart of accounts",
  "ledger.nav.departments": "GL departments",
  "ledger.nav.calendar": "Fiscal calendar",
  "ledger.nav.journals": "Journal entries",
  "ledger.nav.trialBalance": "Trial balance",
  "ledger.nav.glDetail": "General ledger",
  "ledger.nav.inquiry": "Account inquiry",
  "ledger.nav.rules": "Posting rules",
  "ledger.nav.bridge": "Billing bridge",
  "ledger.nav.tieOuts": "Tie-outs",
  "ledger.nav.deferrals": "Deferred revenue",
  "ledger.nav.deposits": "Bank deposits",
  "ledger.nav.settings": "Ledger settings",
  "admin.nav.users": "Users",
  "admin.nav.roles": "Roles",
  "admin.nav.audit": "Audit log",
  "admin.nav.settings": "Club settings",
  "admin.nav.communications": "Communications",
  "emailStatus.badge": "Email sending not activated",
  "emailStatus.body": "Nothing will be emailed until a manager activates it in Club settings, Communications.",
  "admin.nav.installation": "License and updates",
  "teesheet.nav.sheet": "Tee sheet",
  "teesheet.nav.courses": "Courses",
  "teesheet.nav.templates": "Sheet templates",
  "teesheet.nav.rules": "Booking rules",
  "guests.nav.arrivals": "Arrivals",
  "guests.nav.calendar": "Guest calendar",
  "guests.nav.visits": "Guest visits",
  "guests.nav.people": "Guests",
  "guests.nav.policy": "Guest policy",
  "pos.nav.items": "Items",
  "pos.nav.categories": "Categories",
  "pos.nav.modifierGroups": "Modifier groups",
  "pos.nav.outlets": "Outlets",
  "lodging.nav.availability": "Availability",
  "lodging.nav.requests": "Requests",
  "lodging.nav.stays": "Stays",
  "lodging.nav.units": "Units",
  "lodging.nav.setup": "Unit setup",
  "lodging.nav.rates": "Rates",
  "lodging.nav.rules": "Stay rules",
  "lodging.nav.exceptions": "Exceptions",
  "lodging.nav.exceptionsWaiting_one": "{count} folio needs attention",
  "lodging.nav.exceptionsWaiting_other": "{count} folios need attention",
  "lodging.nav.housekeeping": "Housekeeping",
  "lodging.nav.housekeepingTasks": "Housekeeping tasks",
  "lodging.nav.housekeepingSetup": "Housekeeping setup",
  "lodging.nav.jonas": "Jonas import",
  "clubcourse.nav.overview": "Club & course",

  "nav.home": "Home",
  "nav.open": "Open navigation",
  "nav.expand": "Expand menu",
  "nav.collapse": "Collapse menu",
  "menu.today": "Today",
  "menu.settings": "Settings",
  "menu.settings.subtitle": "Everything that is set up once and then left alone.",
  "menu.settings.none": "You have nothing to set up here.",
  "menu.members": "Members",
  "menu.golf": "Golf",
  "menu.diningEvents": "Dining & Events",
  "menu.rooms": "Rooms",
  "menu.guests": "Guests",
  "menu.finance": "Finance",
  "menu.inventory": "Inventory",
  "menu.communications": "Communications",
  "menu.insights": "Insights",
  "menu.memberDirectory": "Member directory",
  "menu.requests": "Requests",
  "menu.stayRequests": "Stay requests",
  "menu.portalAccess": "Portal access",
  "menu.courseInfo": "Course info",
  "menu.paymentsToReview": "Payments to review",
  "menu.needsAttention": "Needs attention",
  "menu.memberAccounts": "Member accounts",
  "menu.minimums": "Food & beverage minimums",
  "menu.statements": "Monthly statements",
  "menu.billsExpenses": "Bills & expenses",
  "menu.bankDeposits": "Bank deposits",
  "menu.generalLedger": "General ledger",
  "menu.accountActivity": "Account activity",
  "menu.dashboards": "Dashboards",
  "menu.customText": "Custom text",
  "menu.staffAccessLog": "Staff access log",
  "menu.voidCompReasons": "Void and comp reasons",
  "menu.bookingRules": "Booking rules",
  "menu.accountingPeriods": "Accounting periods",
  "menu.departments": "Departments",
  "menu.accountMapping": "Account mapping",
  "menu.billingToLedger": "Billing to ledger",
  "menu.reconciliations": "Reconciliations",
  "menu.rangePlanAudit": "Range plan audit",
  "menu.settings.club": "Club",
  "menu.settings.people": "People",
  "menu.settings.members": "Members",
  "menu.settings.golf": "Golf",
  "menu.settings.pos": "Point of sale",
  "menu.settings.rooms": "Rooms",
  "menu.settings.finance": "Finance",
  "menu.settings.communications": "Communications",
  "menu.settings.system": "System",
  "menu.settings.migration": "Migration",
  "module.events": "Events and dining",
  "events.nav.events": "Events",
  "events.nav.spaces": "Spaces",
  "events.nav.setup": "Event setup",
  "events.nav.dining": "Dining",
  "events.nav.diningRequests": "Dining requests",
  "events.nav.diningSetup": "Dining setup",
  "menu.reservations": "Reservations",
  "nav.primary": "Main navigation",

  "notFound.title": "Page not found",
  "notFound.body": "The page you asked for does not exist.",
  "notFound.home": "Go to the home page",

  "theme.label": "Appearance",
  "theme.system": "Match device",
  "theme.light": "Light",
  "theme.dark": "Dark",

  "user.menu": "Account menu for {name}",
  "user.signedInAs": "Signed in as",

  "validation.required": "This field is required.",
  "validation.email": "Enter a valid email address.",
  "validation.money": "Enter an amount such as {example}.",
  "validation.notNegative": "The amount cannot be negative.",
  "validation.percent": "Enter a percentage from 0 to 100 with at most two decimals.",

  "common.save": "Save",
  "common.saved": "Saved.",
  "common.edit": "Edit",
  "common.delete": "Delete",
  "common.remove": "Remove",
  "common.copy": "Copy",
  "common.copied": "Copied to the clipboard.",
  "common.done": "Done",
  "common.back": "Back",
  "common.optional": "optional",
  "common.none": "None",
  "common.all": "All",
  "common.refresh": "Refresh",
  "common.updating": "Updating…",
  "common.yes": "Yes",
  "common.no": "No",

  "account.changePassword": "Change password",
  "account.changePasswordBody": "You stay signed in on this device. Every other device is signed out.",
  "account.currentPassword": "Current password",
  "account.newPassword": "New password",
  "account.confirmPassword": "Repeat the new password",
  "account.passwordHint": "At least {count} characters.",
  "account.passwordShort": "The password needs at least {count} characters.",
  "account.passwordMismatch": "The two passwords do not match.",
  "account.passwordChanged": "Your password was changed.",
  "account.savePassword": "Change password",

  "reset.title": "Set a new password",
  "reset.body": "Enter the code your administrator gave you and choose a new password.",
  "reset.token": "Reset code",
  "reset.submit": "Set password",
  "reset.done": "Your password was set. Sign in with it now.",
  "reset.toSignIn": "Go to sign in",
  "reset.backToSignIn": "Back to sign in",

  "license.unenforced": "License checks off",
  "license.state.valid": "Valid",
  "license.state.grace": "Grace period",
  "license.state.expired": "Expired",
  "license.state.revoked": "Revoked",
  "license.state.invalid": "Invalid",
  "license.state.suspended": "Suspended",
  "license.state.unlicensed": "Not licensed",
  "license.indicatorGrace_one": "Grace: {count} day left",
  "license.indicatorGrace_other": "Grace: {count} days left",
  "license.indicatorReadOnly": "Read-only",
  "license.banner.readOnly": "This installation is read-only because its license is {state}. You can look at everything, but changes are turned off until the license is renewed.",
  "license.banner.grace": "The license has expired and is in its grace period. Everything still works; renew it before the grace period ends.",
  "license.readOnlyShort": "Changes are turned off while the license is {state}.",
  "license.moduleBlockedTitle": "This part of ClubCore is not in your license",
  "license.moduleBlockedBody": "The license for this installation does not include the {module} module. Ask the vendor to add it.",
  "license.openStatus": "Open the license and update details",

  "apiError.not_authenticated": "Your session ended. Sign in again.",
  "apiError.invalid_credentials": "Invalid email or password.",
  "apiError.too_many_attempts": "Too many failed attempts. Try again later.",
  "apiError.staff_only": "Only platform staff can do that.",
  "apiError.missing_permission": "You do not have permission to do that.",
  "apiError.club_required": "Choose a club first.",
  "apiError.no_club_access": "You do not have access to that club.",
  "apiError.club_not_found": "That club could not be found.",
  "apiError.wrong_password": "The current password is not right.",
  "apiError.password_unchanged": "The new password must be different from the current one.",
  "apiError.invalid_reset_token": "This reset code is not valid, or it has expired or been used. Ask your administrator for a new one.",
  "apiError.cannot_reset_self": "You cannot reset your own password here. Use Change password instead.",
  "apiError.shared_account": "This account belongs to more than one club, so only platform staff can reset its password.",
  "apiError.club_slug_taken": "A club with this address name already exists.",
  "apiError.role_not_found": "That role no longer exists.",
  "apiError.role_name_taken": "A role with this name already exists.",
  "apiError.role_in_use": "People still hold this role. Move them to another role first.",
  "apiError.system_role": "Built-in roles cannot be changed or deleted. Create a custom role instead.",
  "apiError.unknown_permissions": "One of the permissions is not known to this version of ClubCore.",
  "apiError.last_manager": "That would leave the club with nobody who can manage users. Give someone else that access first.",
  "apiError.already_in_club": "This person already belongs to the club.",
  "apiError.club_user_not_found": "That person is not a user of this club.",
  "apiError.user_not_found": "That user could not be found.",
  "apiError.session_not_found": "That session could not be found.",
  "apiError.category_name_taken": "A category with this name already exists.",
  "apiError.account_number_taken": "That account number is already in use.",
  "apiError.member_number_taken": "That member number is already in use.",
  "apiError.household_not_found": "That household no longer exists.",
  "apiError.member_not_found": "That member no longer exists.",
  "apiError.category_not_found": "That category no longer exists.",
  "apiError.invalid_cursor": "The list changed while loading. Refresh and try again.",
  "apiError.slot_full": "That tee time does not have enough places left.",
  "apiError.already_booked": "A player is already booked at that time.",
  "apiError.slot_conflict": "That tee time changed while you were working. Try again.",
  "apiError.no_such_tee_time": "There is no tee time at that time on this course.",
  "apiError.rule_violation": "This booking breaks the club's rules.",
  "apiError.bookings_in_block": "Some tee times in that range already have bookings. Move or cancel them first, or allow the block over them.",
  "apiError.booking_not_found": "That booking no longer exists. The sheet has been refreshed.",
  "apiError.booking_cancelled": "That booking was cancelled.",
  "apiError.player_not_found": "That player is no longer in the booking.",
  "apiError.course_not_found": "That course no longer exists.",
  "apiError.starting_point_not_found": "That starting point no longer exists.",
  "apiError.template_not_found": "That template no longer exists.",
  "apiError.waitlist_entry_not_found": "That waiting list entry no longer exists.",
  "apiError.offer_expired": "That offer has expired.",
  "apiError.no_offer": "There is no offer to answer.",
  "apiError.not_open": "That waiting list entry is already closed.",
  "apiError.duplicate": "That conflicts with something that already exists, such as a duplicate name.",
  "apiError.player_checked_in": "That player is already checked in.",
  "apiError.too_many_players": "There are more players than places.",
  "apiError.too_many_entries": "That member has too many open waiting list entries.",
  "apiError.last_player": "A booking needs at least one player. Cancel the booking instead.",
  "apiError.member_not_active": "That member cannot be booked right now.",
  "apiError.course_inactive": "That course is not active.",
  "apiError.in_the_past": "That time has already started.",
  "apiError.status_in_use": "Members or households still have this status. Move them to another status first, then delete it.",
  "apiError.status_code_taken": "A status with this code already exists.",
  "apiError.status_not_found": "That status no longer exists.",
  "apiError.status_unknown": "This club has no such status. Pick one from the list.",
  "apiError.no_active_status": "This club has no active status yet. Add one under Member settings first.",
  "apiError.charge_category_in_use": "A household's recurring charges still use this category. Change those charge lines first, then delete it.",
  "apiError.charge_category_code_taken": "A charge category with this code already exists.",
  "apiError.charge_category_not_found": "That charge category no longer exists.",
  "apiError.billing_arrangement_in_use": "A household's recurring charges still use this arrangement. Change those charge lines first, then delete it.",
  "apiError.billing_arrangement_code_taken": "A billing arrangement with this code already exists.",
  "apiError.billing_arrangement_not_found": "That billing arrangement no longer exists.",
  "apiError.minimum_plan_in_use": "A household still has this minimum plan. Change those households first, then delete it.",
  "apiError.minimum_plan_code_taken": "A minimum plan with this code already exists.",
  "apiError.minimum_plan_not_found": "That minimum plan no longer exists.",
  "apiError.minimum_plan_unknown": "This club has no such minimum plan. Pick one from the list.",
  "apiError.custom_field_in_use": "Members have values for this field, so it cannot be deleted. Archive it instead.",
  "apiError.custom_field_option_in_use": "Members have chosen a choice you removed. Say what replaces it.",
  "apiError.custom_field_type_locked": "The Jonas import fills this field, so its type cannot change.",
  "apiError.custom_field_source_locked": "The Jonas import fills this field, so it cannot be deleted. Archive it instead.",
  "apiError.custom_value_required": "This field is required: change the value, it cannot be cleared.",
  "apiError.custom_field_key_taken": "A custom field with this key already exists.",
  "apiError.custom_field_not_found": "That custom field no longer exists.",
  "apiError.custom_value_not_found": "That member has no value for this field.",
  "apiError.duplicate_slot": "Each slot can be used once.",
  "apiError.duplicate_purpose": "Each kind of mail can appear once.",
  "apiError.address_not_found": "That address no longer exists.",
  "apiError.contact_not_found": "That contact no longer exists.",
  "apiError.note_not_found": "That note no longer exists.",
  "apiError.relationship_not_found": "That relationship no longer exists.",
  "apiError.relationship_exists": "These two members are already related.",
  "apiError.relationship_with_self": "A member cannot be related to themselves.",
  "apiError.account_holder_not_in_household": "The account holder must be a member of this household.",
  "apiError.import_run_not_found": "That import run no longer exists.",
  "apiError.module_not_licensed": "The license for this installation does not include this part of ClubCore.",
  "apiError.license_read_only": "Changes are turned off because the license is {license_status}.",
  "apiError.license_limit_reached": "The license allows {allowed} {limit}, and {used} are in use.",
  "apiError.too_many_requests": "Too many requests. Try again in a moment.",

  "admin.nav.wording": "Wording",

  "wording.toggle": "Edit text on screen",
  "wording.banner": "Edit text on screen is on. Click any outlined text to change its wording. Links and buttons do not work until you turn it off.",
  "wording.done": "Done editing",
  "wording.page.title": "Wording",
  "wording.page.subtitle": "Change the words the staff and member apps use for this club. Your wording replaces the platform's and the built-in text, one language at a time.",
  "wording.page.platformSubtitle": "You are editing the wording every club shows unless it has its own.",
  "wording.page.scope": "Editing wording for",
  "wording.page.scopeClub": "This club",
  "wording.page.scopePlatform": "All clubs (platform)",
  "wording.page.app": "App",
  "wording.page.appStaff": "Staff app",
  "wording.page.appMember": "Member app",
  "wording.page.language": "Language",
  "wording.page.search": "Search wording",
  "wording.page.searchHint": "Finds a word in the built-in text, the changed wording or the message name.",
  "wording.page.module": "Area",
  "wording.page.state": "Show",
  "wording.page.stateAll": "All messages",
  "wording.page.stateOverridden": "Changed here",
  "wording.page.stateInherited": "Changed by the platform",
  "wording.page.table": "Messages and their wording",
  "wording.page.colMessage": "Message",
  "wording.page.colBuiltin": "Built-in text",
  "wording.page.colCurrent": "Wording shown now",
  "wording.page.colAction": "Change",
  "wording.page.edit": "Edit",
  "wording.page.editKey": "Edit {key}",
  "wording.page.sourceBuiltin": "Built-in",
  "wording.page.sourcePlatform": "Platform",
  "wording.page.sourceClub": "This club",
  "wording.page.noMatch": "No messages match these filters",
  "wording.page.empty": "No messages to show",

  "wording.editor.title": "Edit wording",
  "wording.editor.description": "{language}: how this text reads for {club}.",
  "wording.editor.platformDescription": "{language}: how this text reads in every club that has no wording of its own.",
  "wording.editor.pick": "This spot shows more than one piece of text. Choose the one to change.",
  "wording.editor.message": "Message name",
  "wording.editor.builtin": "Built-in text",
  "wording.editor.platform": "Platform wording (all clubs)",
  "wording.editor.clubWording": "Wording for this club",
  "wording.editor.platformWording": "Wording for all clubs",
  "wording.editor.scope": "Apply to",
  "wording.editor.placeholders": "Keep these placeholders. They are filled in with real values:",
  "wording.editor.insert": "Insert {name}",
  "wording.editor.noPlaceholders": "This message has no placeholders.",
  "wording.editor.preview": "How it will read (values are shown in angle brackets)",
  "wording.editor.save": "Save wording",
  "wording.editor.saved": "Wording saved.",
  "wording.editor.resetToPlatform": "Go back to the platform wording",
  "wording.editor.resetToBuiltin": "Go back to the built-in text",
  "wording.editor.resetDone": "Wording reset.",
  "wording.editor.loadFailed": "We could not load this message.",
  "wording.editor.error.empty": "Type some wording, or use the reset button to go back.",
  "wording.editor.error.tooLong": "The wording is too long. It can have at most {limit} characters.",
  "wording.editor.error.html": "The wording is plain text. Remove the HTML tags.",
  "wording.editor.error.characters": "Remove the invisible or control characters.",
  "wording.editor.error.missing": "Keep every placeholder of the original. Missing: {names}.",
  "wording.editor.error.added": "This wording uses placeholders the original does not have: {names}.",
} as const satisfies Record<string, string>;

export type CoreMessages = typeof en;
